import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchSuggestionDetails,
  clearSuggestionDetails,
  selectData,
  selectIsLoading,
  selectSelectedSuggestion,
  selectSuggestionDetails,
  selectDetailsLoading,
  selectResponseType,
  fetchData,
  selectSuggestionsLoading,
  fetchSuggestions,
  setSuggestions,
  selectSuggestionsError,
  resetState,
  setChatbotId,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  selectOpenAiTokens,
  fetchUserData,
  selectPremiumGranted,
  selectGenerationCounts,
  selectActiveSubscription,
  fetchActiveSubscription,
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotInput,
  ChatbotButton,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotError,
  ChatbotSuggestions,
  SuggestionsHeader,
  SuggestionsBox,
  SuggestionItem,
  SuggestionTitle,
  SuggestionDescription,
  SuggestionLink,
  ChatbotDetails,
  ChatbotMessage,
  AppTitle,
  LoadingElement,
} from "../styled";
import styled from "styled-components";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import { SEARCH_SUGGESTIONS_COST, SEARCH_DETAILS_COST } from "../../Utility/tokenCosts";

const GenerationCounter = styled.span`
  font-size: 14px;
  color: ${({ remaining }) => (remaining > 0 ? '#1db954' : '#ff4d4d')};
  margin-right: 15px;
  font-weight: 600;
`;

const SearchLink = styled.a`
  color: #1e90ff;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`;

const SearchEngine = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const suggestionsLoading = useSelector(selectSuggestionsLoading);
  const suggestionsError = useSelector(selectSuggestionsError);
  const suggestions = useSelector(selectData);
  const selectedSuggestion = useSelector(selectSelectedSuggestion);
  const suggestionDetails = useSelector(selectSuggestionDetails);
  const detailsLoading = useSelector(selectDetailsLoading);
  const responseType = useSelector(selectResponseType);
  const premiumGranted = useSelector(selectPremiumGranted);
  const generationCounts = useSelector(selectGenerationCounts);
  const activeSubscription = useSelector(selectActiveSubscription);

  const [query, setQuery] = useState("");
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [openAiTokens, setOpenAiTokensState] = useState(0);
  const currentTokens = useSelector(selectOpenAiTokens);

  const validationToastId = 'search-engine-validation-toast';
  const limitToastId = 'search-engine-limit-toast';

  const STANDARD_MONTHLY_ID = process.env.REACT_APP_STANDARD_MONTHLY;
  const STANDARD_YEARLY_ID = process.env.REACT_APP_STANDARD_YEARLY;
  
  const isStandardPlan = activeSubscription && 
    (activeSubscription.priceId === STANDARD_MONTHLY_ID || activeSubscription.priceId === STANDARD_YEARLY_ID);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(setChatbotId("searchengine"));
    dispatch(clearSuggestionDetails());
    dispatch(resetState());
    dispatch(fetchData());
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          dispatch(fetchUserData());
          dispatch(fetchActiveSubscription());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600);
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600);
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const searchGenerations = generationCounts?.search || 0;
  const remainingGenerations = 5 - searchGenerations;

  useEffect(() => {
    if (isStandardPlan) {
      const toastOptions = {
        toastId: limitToastId,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      };

      const message = `Limit wyszukiwań w planie Standard: ${remainingGenerations}/5`;

      if (toast.isActive(limitToastId)) {
        toast.update(limitToastId, { render: message });
      } else {
        toast.info(message, toastOptions);
      }
    }
  }, [isStandardPlan, remainingGenerations]);

  const handleChange = (e) => setQuery(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) {
      dispatch(setSuggestions("Proszę wpisać zapytanie."));
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < SEARCH_SUGGESTIONS_COST) {
      toast.error("Masz za mało tokenów, aby wyszukać.", toastOptions);
      return;
    }

    dispatch(fetchSuggestions());
    dispatch({ type: "FETCH_SEARCH_SUGGESTIONS", payload: query });
    dispatch(clearSuggestionDetails());
  };

  const handleSuggestionClick = (index) => {
    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < SEARCH_DETAILS_COST) {
      toast.error("Masz za mało tokenów, aby wygenerować streszczenie.", toastOptions);
      return;
    }

    if (selectedSuggestion === index) {
      dispatch(clearSuggestionDetails());
    } else {
      dispatch(fetchSuggestionDetails(index));
    }
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((prev) => !prev);

  const renderClickableLinks = (text) => {
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = markdownLinkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      const [fullMatch, linkText, url] = match;
      parts.push(
        <SearchLink key={url + lastIndex} href={url} target="_blank" rel="noopener noreferrer">
          {linkText}
        </SearchLink>
      );
      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>);
  };

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage isHighZIndex={true} />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer>
            <ChatbotMain
              isDetailsExpanded={isDetailsExpanded}
              isDetailsPanelVisible={selectedSuggestion !== null}
            >
              <AppTitle>Wyszukiwarka</AppTitle>
              <ChatbotForm onSubmit={handleSubmit}>
                <ChatbotInput
                  type="text"
                  value={query}
                  onChange={handleChange}
                  placeholder="Wpisz czego szukasz..."
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isStandardPlan && (
                    <GenerationCounter remaining={remainingGenerations}>
                      {remainingGenerations}/5
                    </GenerationCounter>
                  )}
                  <ChatbotButton type="submit" disabled={suggestionsLoading} tokenCost={SEARCH_SUGGESTIONS_COST}>
                    {suggestionsLoading ? "Szukanie..." : "Szukaj"}
                  </ChatbotButton>
                </div>
              </ChatbotForm>
              {suggestionsLoading ? (
                <LoadingElement>Wyszukiwanie...</LoadingElement>
              ) : suggestionsError ? (
                <ChatbotError>{suggestionsError}</ChatbotError>
              ) : suggestions && responseType === "message" ? (
                <ChatbotMessage>{suggestions}</ChatbotMessage>
              ) : Array.isArray(suggestions) && suggestions.length > 0 ? (
                <ChatbotSuggestions>
                  <SuggestionsHeader>Wyniki wyszukiwania:</SuggestionsHeader>
                  <SuggestionsBox>
                    {suggestions.map((suggestion, index) => (
                      <SuggestionItem
                        key={index}
                        onClick={() => handleSuggestionClick(index)}
                        isSelected={selectedSuggestion === index}
                        tokenCost={SEARCH_DETAILS_COST}
                      >
                        <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                        <SuggestionDescription>
                          {suggestion.description.length > 150
                            ? `${suggestion.description.substring(0, 150)}...`
                            : suggestion.description}
                        </SuggestionDescription>
                        <SuggestionLink href={suggestion.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          {suggestion.link}
                        </SuggestionLink>
                      </SuggestionItem>
                    ))}
                  </SuggestionsBox>
                </ChatbotSuggestions>
              ) : (
                <ChatbotMessage>
                  Wpisz zapytanie, aby rozpocząć wyszukiwanie.
                </ChatbotMessage>
              )}
            </ChatbotMain>
            <ChatbotDetailsPanel isDetailsExpanded={isDetailsExpanded}>
              <ChatbotDetailsHeader isDetailsExpanded={isDetailsExpanded} />
              <ChatbotExpandButton
                isDetailsExpanded={isDetailsExpanded}
                onClick={toggleDetailsPanel}
              >
                {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
              </ChatbotExpandButton>
              {selectedSuggestion !== null && suggestions.length > 0 ? (
                detailsLoading ? (
                  <LoadingElement>Generowanie streszczenia...</LoadingElement>
                ) : suggestionDetails ? (
                  <ChatbotDetails>
                    <SuggestionsHeader>
                      {suggestions[selectedSuggestion].title}
                    </SuggestionsHeader>
                    <div>{renderClickableLinks(suggestionDetails)}</div>
                  </ChatbotDetails>
                ) : (
                  <ChatbotError>
                    Wystąpił błąd podczas ładowania streszczenia.
                  </ChatbotError>
                )
              ) : (
                <ChatbotMessage>
                  Wybierz wynik, aby zobaczyć streszczenie.
                </ChatbotMessage>
              )}
            </ChatbotDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default SearchEngine; 